import React, { useMemo } from 'react';
import BtnFilled from '../../components/BtnFilled';
import BtnOutlined from '../../components/BtnOutlined';
import { Table } from '../../components';
import { CAMPAIGN_STATUS, STATUS_META } from './constants';
import { getCampaignDevicesColumns, getFirmwareVersionColumns } from './columns';
import { formatDateTime, getDeviceRowStatus, groupFirmwareByVersion, isNewerVersion } from './utils';
import CampaignProgress from './CampaignProgress';

const CampaignState = ({ status, firmware, firmwareError, onVerify, onPromote, onCancel, onExclude, onRefresh, actionLoading, excludingId }) => {
  const campaign = status?.campaign;
  const campaignStatus = campaign?.status;
  const meta = STATUS_META[campaignStatus] || {};
  const canPromote = campaignStatus === CAMPAIGN_STATUS.READY;
  const showVerify = campaignStatus === CAMPAIGN_STATUS.VERIFY_PENDING;

  const details = [
    { label: 'Целевая версия', value: status?.target_version },
    { label: 'Тестовая-1', value: campaign?.version },
    { label: 'Тестовая-2', value: campaign?.verify_version },
    { label: 'Старт', value: campaign?.started_at ? formatDateTime(campaign.started_at) : null },
  ].filter((item) => item.value !== null && item.value !== undefined && item.value !== '');

  const versionGroups = useMemo(() => (showVerify ? groupFirmwareByVersion(firmware) : []), [firmware, showVerify]);

  const deviceColumns = useMemo(() => getCampaignDevicesColumns({ onExclude, actionLoading, excludingId }), [actionLoading, excludingId, onExclude]);

  const firmwareColumns = useMemo(
    () =>
      getFirmwareVersionColumns({
        actionTitle: 'Запустить второй этап',
        onAction: onVerify,
        isDisabled: (row) => !isNewerVersion(row.version, campaign?.version),
        actionLoading,
      }),
    [actionLoading, campaign?.version, onVerify]
  );

  return (
    <>
      <div className="max-w-7xl mx-auto bg-white shadow-md rounded-3xl p-4 mt-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="text-xs uppercase text-gray-400 mb-1">Этап {status?.stage || campaign?.stage}</div>
            <h3 className="text-base sm:text-lg font-medium text-gray-700">{meta.title}</h3>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600 mt-3">
              {details.map(({ label, value }) => (
                <span key={label}>
                  {label}: <span className="font-medium text-gray-800">{value}</span>
                </span>
              ))}
            </div>
          </div>
          <div className="flex gap-3 sm:min-w-[320px]">
            <BtnOutlined title="Обновить" btnCb={onRefresh} disabled={actionLoading} />
            {!canPromote && <BtnOutlined title="Отменить обкатку" btnCb={onCancel} disabled={actionLoading} />}
          </div>
        </div>

        <CampaignProgress status={status} />

        {showVerify && <p className="text-sm text-gray-600 mt-5">Второй этап можно запустить только на версии новее {campaign?.version}.</p>}

        <BtnFilled title="Выкатить на весь парк" className="mt-5 ml-auto block w-fit min-w-[220px]" disabled={!canPromote || actionLoading} btnCb={onPromote} />
      </div>

      {showVerify && <Table columns={firmwareColumns} data={versionGroups} error={firmwareError} rowKey="version" />}

      <Table
        columns={deviceColumns}
        data={status?.devices ?? []}
        rowKey="device_id"
        getRowClassName={(row) => (getDeviceRowStatus(row) === 'pending' ? 'bg-orange-100/70 hover:bg-orange-100' : null)}
      />
    </>
  );
};

export default CampaignState;
